/**
 * GDSStateManager.js
 * Manages the state of the GDS Builder, including context, step, and URL interactions.
 */
export class GDSStateManager {
    constructor() {
        this.initState();
    }

    /**
     * Initializes or resets the component's state properties.
     */
    initState() {
        this.context = {};
        this.currentStep = 1;
        this.isLoading = false;
        this.currentField = '';
        this.isInitializing = true;
        this.completedSteps = [];
        this.wasStep2Skipped = false;
        this.currentSharedConfigId = null;

        // Flags to control URL and history updates
        this._preventURLUpdates = false;
        this._isNavigatingBackProgrammatically = false;
        this.isUpdatingViaSnapshot = false;
        this._isUpdatingHistory = false;
        this._forcedStep = null;
        this._isStepTransition = false;

        // Properties loaded from URL parameters
        this.sharedConfigIdFromUrl = null;
        this.contextFromUrl = null;
        this.selectionsFromUrl = null;
        this.stepFromUrl = null;
        this.urlSelections = null;
    }

    /**
     * Sets the loading state.
     * @param {boolean} isLoading
     */
    setLoading(isLoading) {
        this.isLoading = isLoading;
    }

    /**
     * Updates the current step and the completed steps array.
     * @param {number} newStep - The step to move to.
     * @param {number|null} oldStep - The step being moved from, to mark as complete.
     */
    setStep(newStep, oldStep = null) {
        if (oldStep && newStep > oldStep && !this.completedSteps.includes(oldStep)) {
            this.completedSteps.push(oldStep);
        }
        this.currentStep = newStep;
    }

    /**
     * Forces a specific step, overriding normal step determination
     * @param {number} step - The step to force
     */
    forceStep(step) {
        this._forcedStep = step;
    }

    /**
     * Processes the API response to update the state.
     * @param {Object} responseData - The data received from the API service.
     */
    updateStateFromApiResponse(responseData) {
        // Add a defensive check to ensure responseData is a valid object.
        if (!responseData || typeof responseData !== 'object') {
            console.error("Invalid responseData passed to state manager.", responseData);
            this.context = {}; // Reset context to prevent errors
            return;
        }

        this.context = responseData;
        this.currentField = Array.isArray(responseData.field) ? responseData.field[0] : responseData.field;
        this.updateCompletedStepsFromContext();

        // Check for forced step first
        if (this._forcedStep) {
            this.currentStep = this._forcedStep;
            this._forcedStep = null;
            return;
        }

        // Determine step from page_type
        switch (responseData.page_type) {
            case 'dropdowns':
                this.currentStep = 1;
                break;
            case 'environment':
                this.currentStep = 2;
                break;
            case 'card_selection':
                this.currentStep = 3;
                break;
            // 'question' page_type is handled by its context field
            case 'question':
                if (['Device Make', 'Device Model'].includes(this.currentField)) {
                    this.currentStep = 1;
                } else if (this.currentField === 'environment') {
                    this.currentStep = 2;
                }
                break;
        }
    }

    /**
     * Updates the list of completed steps based on the current context.
     */
    updateCompletedStepsFromContext() {
        this.completedSteps = [];
        const ctx = this.context.context || {};
        if (ctx['Device Make'] && ctx['Device Model']) {
            this.completedSteps.push(1);
        }
        if (ctx['environment']) {
            this.completedSteps.push(2);
        }
    }

    /**
     * Parses the browser's URL search parameters to initialize state.
     */
    loadStateFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        this.sharedConfigIdFromUrl = urlParams.get('sharedConfigId');
        this.selectionsFromUrl = null;
        this.contextFromUrl = null;

        if (this.sharedConfigIdFromUrl) {
            this.stepFromUrl = null; // Snapshot ID takes precedence over step param
        } else {
            this.stepFromUrl = urlParams.get('step') ? parseInt(urlParams.get('step'), 10) : null;
            try {
                const contextParam = urlParams.get('context');
                if (contextParam) this.contextFromUrl = JSON.parse(decodeURIComponent(contextParam));

                const selectionsParam = urlParams.get('selections');
                if (selectionsParam) this.selectionsFromUrl = JSON.parse(decodeURIComponent(selectionsParam));
            } catch (e) {
                console.error('Error parsing context/selections from URL:', e);
            }
        }
    }

    /**
     * Generates a configuration object suitable for creating a snapshot.
     * @param {Function} buildSelectionsForURL - A function passed from the card manager to get selections.
     * @returns {Object|null}
     */
    getCurrentConfigurationForSnapshot(buildSelectionsForURL) {
        const currentBuilderContext = this.context?.context || {};

        // Validation based on the current step
        switch (this.currentStep) {
            case 1:
                console.warn('[Snapshot] Cannot create snapshot on Step 1.');
                return null;
            case 2:
                if (!currentBuilderContext['Device Make'] || !currentBuilderContext['Device Model']) {
                    console.error('[Snapshot] Validation FAILED for Step 2. Required: Device Make & Model.', {
                        'Device Make': currentBuilderContext['Device Make'],
                        'Device Model': currentBuilderContext['Device Model']
                    });
                    return null;
                }
                break;
            case 3:
                if (!currentBuilderContext['Device Make'] || !currentBuilderContext['Device Model']) {
                    console.error('[Snapshot] Validation FAILED for Step 3. Required: Device Make & Model.', {
                        'Device Make': currentBuilderContext['Device Make'],
                        'Device Model': currentBuilderContext['Device Model']
                    });
                    return null;
                }
                if (!this.wasStep2Skipped && !currentBuilderContext['environment']) {
                    console.error('[Snapshot] Validation FAILED for Step 3. Environment is missing and was not skipped.');
                    return null;
                }
                break;
            default:
                console.warn(`[Snapshot] Attempted to create snapshot on unknown step: ${this.currentStep}`);
                return null;
        }

        const snapshotConfig = { ...currentBuilderContext };

        if (this.currentStep === 3 && typeof buildSelectionsForURL === 'function') {
            const selections = buildSelectionsForURL();
            if (selections && (Object.keys(selections.main_cards || {}).length > 0 ||
                                  Object.keys(selections.additional_cards || {}).length > 0)) {
                snapshotConfig.selections = selections;
            }
        }

        return snapshotConfig;
    }

    /**
     * Updates the browser URL with a new snapshot ID.
     * @param {string} shortId - The short snapshot ID from the API.
     * @param {string} fullId - The full snapshot ID from the API.
     * @param {boolean} useReplace - Whether to use `history.replaceState` instead of `pushState`.
     */
    updateUrlWithSnapshot(shortId, fullId, useReplace = false) {
        if (this._preventURLUpdates || this.isUpdatingViaSnapshot) return;

        this.isUpdatingViaSnapshot = true;
        this.currentSharedConfigId = shortId;

        const url = new URL(window.location.href);
        url.searchParams.delete('context');
        url.searchParams.delete('selections');
        url.searchParams.delete('step');
        url.searchParams.set('sharedConfigId', shortId);

        const stateObj = {
            sharedConfigId: shortId,
            fullSnapshotId: fullId,
            context: this.context?.context || {},
            step: this.currentStep,
            wasStep2Skipped: this.wasStep2Skipped
        };

        const historyMethod = useReplace ? 'replaceState' : 'pushState';
        window.history[historyMethod](stateObj, '', url.toString());

        this.isUpdatingViaSnapshot = false;
    }

    /**
     * Updates the URL with context parameters (non-snapshot mode)
     * @param {boolean} useReplace - Whether to use replaceState instead of pushState
     * @param {Function} buildSelectionsForURL - Optional function to build selections
     */
    updateURLWithContext(useReplace = false, buildSelectionsForURL = null) {
        // Don't use traditional URL updates in step 3 - use snapshots instead
        if (this.currentStep === 3) {
            return;
        }

        if (this._preventURLUpdates || this._isUpdatingHistory) {
            console.warn("[updateURLWithContext] URL update prevented");
            return;
        }

        this._isUpdatingHistory = true;

        try {
            const contextToStore = this.context?.context || {};
            let selectionsToStore = null;

            // Only add selections for non-step-3 scenarios
            if (this.currentStep === 3 && buildSelectionsForURL) {
                selectionsToStore = buildSelectionsForURL();
            }

            const url = new URL(window.location.href);

            // Clear snapshot-related params when using traditional context URLs
            url.searchParams.delete('sharedConfigId');

            if (Object.keys(contextToStore).length > 0) {
                const contextParam = encodeURIComponent(JSON.stringify(contextToStore));
                url.searchParams.set('context', contextParam);
            } else {
                url.searchParams.delete('context');
            }

            if (selectionsToStore && Object.keys(selectionsToStore).length > 0) {
                const selectionsParam = encodeURIComponent(JSON.stringify(selectionsToStore));
                url.searchParams.set('selections', selectionsParam);
            } else {
                url.searchParams.delete('selections');
            }

            url.searchParams.set('step', this.currentStep);

            const stateObj = {
                context: contextToStore,
                step: this.currentStep,
                wasStep2Skipped: this.wasStep2Skipped
            };

            // Avoid pushing identical states
            const currentState = history.state;
            const currentURL = window.location.href;
            let isIdentical = false;

            if (currentState) {
                isIdentical = currentState.step === stateObj.step &&
                            currentState.wasStep2Skipped === stateObj.wasStep2Skipped &&
                            JSON.stringify(currentState.context) === JSON.stringify(stateObj.context) &&
                            url.toString() === currentURL;
            }

            if (!useReplace && isIdentical) {
                console.warn("[updateURLWithContext] Skipping identical state push");
                return;
            }

            const historyMethod = useReplace ? 'replaceState' : 'pushState';
            window.history[historyMethod](stateObj, '', url.toString());

        } catch (error) {
            console.error('Error updating URL with context:', error);
        } finally {
            this._isUpdatingHistory = false;
        }
    }

    /**
     * Creates a state object from a history event
     * @param {PopStateEvent} event - The popstate event
     * @returns {Object} - Parsed state object
     */
    parseHistoryState(event) {
        if (!event.state ||
            (event.state.step === undefined && event.state.sharedConfigId === undefined) ||
            (event.state.sharedConfigId === undefined && event.state.context === undefined)) {
            return null;
        }

        return {
            sharedConfigId: event.state.sharedConfigId,
            context: event.state.context || {},
            step: parseInt(event.state.step) || 1,
            wasStep2Skipped: event.state.wasStep2Skipped || false,
            selections: event.state.selections
        };
    }

    /**
     * Determines if we should skip step 2 when navigating back
     * @param {number} fromStep - Current step
     * @param {number} toStep - Target step
     * @returns {boolean}
     */
    shouldSkipStep2OnBack(fromStep, toStep) {
        return fromStep === 3 && toStep === 2 && this.wasStep2Skipped === true;
    }

    /**
     * Sets the flag to prevent URL updates during operations
     * @param {boolean} prevent
     */
    setPreventURLUpdates(prevent) {
        this._preventURLUpdates = prevent;
    }

    /**
     * Checks if we're currently preventing URL updates
     * @returns {boolean}
     */
    isPreventingURLUpdates() {
        return this._preventURLUpdates;
    }

    /**
     * Sets the programmatic navigation flag
     * @param {boolean} isNavigating
     */
    setProgrammaticNavigation(isNavigating) {
        this._isNavigatingBackProgrammatically = isNavigating;
    }

    /**
     * Checks if we're currently in programmatic navigation
     * @returns {boolean}
     */
    isProgrammaticNavigation() {
        return this._isNavigatingBackProgrammatically;
    }

    /**
     * Determines if a snapshot should be created based on current state
     * @returns {boolean}
     */
    shouldCreateSnapshot() {
        // Don't create snapshots before step 2
        if (this.currentStep < 2) return false;

        const context = this.context?.context || {};

        // Step 2: Need device make and model
        if (this.currentStep === 2) {
            return context['Device Make'] && context['Device Model'];
        }

        // Step 3: Need device make and model, environment optional if step 2 was skipped
        if (this.currentStep === 3) {
            const hasDeviceInfo = context['Device Make'] && context['Device Model'];
            if (!hasDeviceInfo) return false;

            // If step 2 wasn't skipped, we should have environment
            if (!this.wasStep2Skipped) {
                return !!context['environment'];
            }

            // If step 2 was skipped, environment is optional
            return true;
        }

        return false;
    }
}
